import React, { ReactNode, useEffect, useRef } from 'react'
import clsx from 'clsx'
import { InputBaseProps, MenuItem, Typography, Link } from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning';
import { Tooltip, Spinner, TextField, TextFieldAdornmentPrefix, TextFieldAdornmentSuffix, BooleanField, SelectField, RichTextField, RichTextFieldToolbarType, ButtonField, ImageField, TagField, DateField } from 'components'
import { makeStyles } from '@material-ui/core'
import { Color, FontSize, FontWeight } from 'theme/style'
import spacing from 'theme/config/spacing';
import _ from 'lodash';
import { config } from 'app/config'
import DateRangeIcon from '@material-ui/icons/DateRange';
import { Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: spacing(0.5),

    },
  },

  infoContainer: {
    position: 'relative',
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  },
  infoContainerVertical: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& >p': {
      marginBottom: theme.spacing(1),
    }
  },
  infoContainerHorizontal: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(1)
  },
  label: {
    fontWeight: FontWeight.Medium,
    fontSize: FontSize.Body1,
    color: Color.TextPrimary,
    textAlign: 'left'
  },
  link: {
    fontWeight: FontWeight.Regular,
    fontSize: FontSize.Body1,
    color: Color.Secondary,
    textAlign: 'right'
  },
  descriptionContainer: {
  },
  description: {
    marginTop: spacing(1),
    color: Color.TextSecondary,
    fontSize: FontSize.Body2,
  },
  fieldContainer: {
    alignItems: 'flex-end',
    display: 'flex',
    justifyContent: 'flex-end',
    minHeight: '45px',
    width: '100%',
    flex: 1,
    position: 'relative',
    '& >div': {
      width: '100%',
      justifyContent: 'flex-start',
    }
  },
  endIconContainer: {
    color: Color.TextPrimary,
    width: '45px !important',
    height: '45px',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center !important',
    right: 0,
    top: 0,
    zIndex: 999,
  },
  disabledEndIcon: {
    pointerEvents: 'none'
  },
  errorIcon: {
    color: Color.Warning,
    width: '2.2rem !important',
    height: '2.2rem',
    zIndex: 999
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    height: '100%',
    top: 0,
    left: 0,
    width: '100%',
  }
}))

export type FieldError = {
  error: boolean
  errorMessage: string
}

export type FieldsListErrors = { [key: string]: FieldError[] }


interface CardFieldProps extends InputBaseProps {
  className?: string
  isLoading?: boolean
  layout?: 'vertical' | 'horizontal'

  //The type of each field supported.
  type?: 'text' | 'textarea' | 'password' | 'email' | 'number' | 'select' | 'boolean' | 'richtext' | 'button' | 'image' | 'tags' | 'date'

  //Defines if the field should autosave when changed.
  autosave?: boolean

  //Common props.
  name: string
  label?: string | ReactNode
  placeholder?: string
  value?: any
  description?: string
  errors?: FieldsListErrors

  //Common methods.
  //Triggered when the field changed value.
  onUpdate?: (name: string, value: any, action?: string, object?: any) => void

  //Triggered when the field confirmed the change (or when autosaved).
  onConfirm?: (name: string, value: any) => void

  //Triggered when the field has been clicked (used on specific types such as Button and Image).
  onClick?: () => void

  //Triggered when the field has been removed its value (used on specific types such as Button and Image).
  onRemove?: () => void

  //Defines an icon placed at the end of the field.
  endIcon?: ReactNode

  //Defines a prefix and a suffix (used in almost every field type).
  prefix?: string
  suffix?: string

  //Type Select specific props
  options?: { key: string, value: any, name: string }[]
  enableClear?: boolean

  //Type Tags specific props
  items?: any[]
  activeItems?: any[]
  itemValueConstructor?: (item: any) => string

  //Type RichText specific props
  richTextToolbarType?: RichTextFieldToolbarType

  //Type Button specific props
  enableEdit?: boolean
  actions?: React.ReactNode

  //Link props
  link?: {
    text: string
    url: string
  }
}

const CardField = ({ ...props }: CardFieldProps) => {
  const classes = useStyles()



  //DEFAULTS

  let layoutClass = clsx(classes.infoContainer, classes.infoContainerVertical)
  if (props.layout === 'horizontal') layoutClass = clsx(classes.infoContainer, classes.infoContainerHorizontal)
  if (props.layout === 'vertical') layoutClass = clsx(classes.infoContainer, classes.infoContainerVertical)



  //AUTOSAVE

  const autosaveDefaultValue = true
  const autosaveValue = props.autosave ?? autosaveDefaultValue

  //Automatically triggers the search confirm after a delay while typing.
  var timer: NodeJS.Timeout
  const data: any = useRef(null)
  data.current = props.onConfirm

  useEffect(() => {
    if (autosaveValue === false) return
    if (props.onConfirm == null) return
    if (props.name == null) return

    timer = setTimeout(() => {
      if (timer) clearTimeout(timer)
      data.current(props.name, props.value)
    }, config.automation.autosave.delay)
    return () => clearTimeout(timer)

  }, [props.value])



  //FIELD UPDATES

  function updateField(name: string, value: any, action?: string, object?: any) {
    if (props.onUpdate == null) return
    props.onUpdate(name, value, action, object)
  }

  function confirmField(name: string, value: any) {
    if (props.onConfirm == null) return
    props.onConfirm(name, value)
  }



  //RENDER

  function renderField(): ReactNode {
    if (props.isLoading != null && props.isLoading === true) return <div className={classes.loading}><Spinner type={'small'} /></div>
    return render()

    function render(): ReactNode {
      if (props.type == null) return <></>

      //Defines all common props passed to the different field types.
      let allProps: any = {
        name: props.name,
        placeholder: props.placeholder,
        value: props.value,
        onUpdate: updateField,
        onConfirm: confirmField,
      }

      if (props.prefix != null) {
        const adornment = <TextFieldAdornmentPrefix text={props.prefix} />
        allProps['startAdornment'] = adornment
      }

      if (props.suffix != null) {
        const adornment = <TextFieldAdornmentSuffix text={props.suffix} />
        allProps['endAdornment'] = adornment
      }

      //Renders the "Text" type.
      if (props.type === 'text') return <TextField {...allProps} />

      //Renders the "Textarea" type.
      if (props.type === 'textarea') return <TextField {...allProps} multiline />

      //Renders the "Password" type.
      if (props.type === 'password') return <TextField {...allProps} type={'password'} />

      //Renders the "Number" type.
      if (props.type === 'number') return <TextField {...allProps} type={'number'} />

      //Renders the "Boolean" type.
      if (props.type === 'boolean') return <BooleanField {...allProps} />

      //Renders the "Rich Text" type.
      if (props.type === 'richtext') return <RichTextField {...allProps} toolbarType={props.richTextToolbarType} />

      //Renders the "Button" type.
      if (props.type === 'button') return <ButtonField {...allProps} enableEdit={props.enableEdit} enableClear={props.enableClear} actions={props.actions} onClick={props.onClick} onRemove={props.onRemove} />

      //Renders the "Image" type.
      if (props.type === 'image') return <ImageField {...allProps} onClick={props.onClick} onRemove={props.onRemove} />

      //Renders the "Tags" type.
      if (props.type === 'tags') return <TagField {...allProps} items={props.items ?? []} activeItems={props.activeItems ?? []} itemValueConstructor={props.itemValueConstructor} />

      //Renders the "Date" type.
      if (props.type === 'date') return <DateField {...allProps} />

      //Renders the "Select" type.
      if (props.type === 'select') return <SelectField {...allProps} enableClear={props.enableClear}>
        {props.options?.map((o) => (
          <MenuItem key={o.key} value={o.value}>{o.name}</MenuItem>
        ))}
      </SelectField>
    }
  }

  function renderIcon(): ReactNode {
    if (props.errors == null) return renderEndIcon()

    const errorList = props.errors[props.name]
    const hasErrors = _.find(errorList, 'error')

    //Renders the end icon, if present.
    if (hasErrors == null) return renderEndIcon()

    //Renders the error icon.
    return renderErrorIcon()

    function renderEndIcon(): ReactNode {
      if (props.type === 'date') return <div className={clsx(classes.endIconContainer, classes.disabledEndIcon)}><DateRangeIcon /></div>
      if (props.endIcon == null) return <></>
      return <div className={classes.endIconContainer}>{props.endIcon}</div>
    }

    function renderErrorIcon(): ReactNode {
      let errorNodes: ReactNode[] = []
      errorList.forEach(e => {
        if (e.error === true) errorNodes.push(<div>• {e.errorMessage}</div>)
      })

      return <div className={classes.endIconContainer}><Tooltip title={<>{errorNodes.map(e => e)}</>} placement="top-end"><WarningIcon className={classes.errorIcon} /></Tooltip></div>
    }
  }

  return (
    <div className={clsx(props.className, classes.container)}>
      <div className={layoutClass}>
        <div className={clsx(classes.labelContainer)}>
          {props.label != null && <Typography className={classes.label} variant="body1">{props.label}</Typography>}
          {props.link != null && <Link component={RouterLink} to={props.link.url} className={classes.link} variant="body1">{props.link.text}</Link>}
        </div>

        <div className={clsx(classes.fieldContainer)}>
          {renderField()}
          {renderIcon()}
        </div>
      </div>
      <div className={clsx(classes.descriptionContainer)}>
        {props.description != null && (<Typography className={classes.description} variant="body2">{props.description}</Typography>)}
      </div>
    </div>
  )
}

export default CardField
